import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Nav, Navbar, NavDropdown } from "react-bootstrap";
import ComponentContext from "../Context";
import Pajoohyar from "../Images/Pajoohyar.png";
import getSuccessfulLoginUrl, {
  SUCCESSFUL_LOGIN_EVENT,
  clearSuccessfulLoginData,
  getStoredSuccessfulLoginData,
  getStoredProfileData,
  normalizeProfileImageUrl,
  setPostLoginRedirect,
} from "../lib/auth";

const DEFAULT_AVATAR_URL =
  "https://cdn.inoor.ir/UC/DefaultImage/PersonPic.medium.jpg";

const Header = () => {
  const context = useContext(ComponentContext);
  const loginUrl = getSuccessfulLoginUrl();
  const [storedLoginData, setStoredLoginData] = useState(
    getStoredSuccessfulLoginData(),
  );
  const [storedProfileData, setStoredProfileData] = useState(
    getStoredProfileData(),
  );
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const avatarMenuRef = useRef(null);
  const [avatarSrc, setAvatarSrc] = useState(DEFAULT_AVATAR_URL);
  const logoutExecutedRef = useRef(false);
  const storedLoginKey = storedLoginData?.key;
  const storedLoginUserId = storedLoginData?.userId;
  // ApiDriver is hydrated only when the API token is present and unexpired.
  // Use it as the single source of truth for both the avatar and login link.
  const hasApiCredentials = Boolean(context?.ApiDriver?.access_token);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateStoredData = () => {
      setStoredLoginData(getStoredSuccessfulLoginData());
      setStoredProfileData(getStoredProfileData());
    };

    window.addEventListener("storage", updateStoredData);
    window.addEventListener(SUCCESSFUL_LOGIN_EVENT, updateStoredData);

    return () => {
      window.removeEventListener("storage", updateStoredData);
      window.removeEventListener(SUCCESSFUL_LOGIN_EVENT, updateStoredData);
    };
  }, []);

  // بررسی URL برای خروج از کتابخانه وب
  useEffect(() => {
    if (typeof window === "undefined" || logoutExecutedRef.current) return;

    const pathname = window.location.pathname;
    if (pathname === "/logout-from-web-library") {
      logoutExecutedRef.current = true;

      const currentUrl =
        typeof window !== "undefined" ? window.location.href : "/";
      const encodedLogoutUrl = storedLoginData?.idToken
        ? `https://auth.inoor.ir/connect/endsession?id_token_hint=${encodeURIComponent(
            storedLoginData.idToken,
          )}&post_logout_redirect_uri=https://pajoohyar.ir`
        : currentUrl;

      setAvatarMenuOpen(false);
      if (context) {
        context.cookie.remove("pajoohyar_access_token");
        context.cookie.remove("pajoohyar_expires_at");
        context.cookie.remove("pajoohyar_profile");
        if (context.ApiDriver) {
          context.ApiDriver.access_token = null;
          context.ApiDriver.expires_at = null;
        }
        context.profile = null;
      }

      clearSuccessfulLoginData();
      setStoredLoginData(null);
      setStoredProfileData(null);

      window.location = encodedLogoutUrl;
    }
  }, [storedLoginData, context]);

  const getProfileFullName = profileData => {
    if (!profileData) return "";
    const nameCandidates = [
      profileData.user_full_name,
      profileData.displayName,
      profileData.name,
      profileData.preferred_username,
      profileData.username,
      profileData.given_name,
    ];
    const resolved = nameCandidates.find(
      value => value && String(value).trim(),
    );
    return resolved ? String(resolved).trim() : "";
  };

  const getProfileAvatar = profileData => {
    if (!profileData) return null;
    const avatarCandidates = [
      profileData.user_full_path_picture,
      profileData.full_path_picture,
      profileData.picture,
    ];
    return avatarCandidates.map(normalizeProfileImageUrl).find(Boolean) || null;
  };

  useEffect(() => {
    if (context.profile || !storedProfileData) return;

    const resolvedName = getProfileFullName(storedProfileData) || "کاربر";

    context.profile = {
      full_name: resolvedName,
      first_name: resolvedName,
      last_name: "",
      user_id: storedLoginData?.userId,
      zotero_key: storedLoginData?.key,
      avatar: getProfileAvatar(storedProfileData),
    };
  }, [context, storedProfileData, storedLoginData]);

  const derivedProfile = useMemo(() => {
    if (context.profile) {
      if (typeof context.profile === "string") {
        try {
          const parsed = JSON.parse(context.profile);
          if (parsed && typeof parsed === "object") {
            context.profile = parsed;
            return parsed;
          }
        } catch (error) {
          // ignore parse errors and fall back to stored data
        }
      }

      if (typeof context.profile === "object") {
        return context.profile;
      }
    }

    const profileFullName = getProfileFullName(storedProfileData);
    const profileAvatar = getProfileAvatar(storedProfileData);

    if (storedProfileData && (profileFullName || profileAvatar)) {
      return {
        full_name: profileFullName || "کاربر",
        avatar: profileAvatar,
      };
    }

    if (storedLoginKey || storedLoginUserId) {
      return {
        full_name: "کاربر",
        avatar: null,
      };
    }

    return null;
  }, [
    context.profile,
    storedProfileData,
    storedLoginKey,
    storedLoginUserId,
  ]);

  const isLoggedIn = hasApiCredentials;
  const welcomeName =
    (derivedProfile?.full_name && derivedProfile.full_name.trim()) || "کاربر";
  const avatarUrl =
    derivedProfile?.avatar && derivedProfile.avatar.trim()
      ? derivedProfile.avatar.trim()
      : null;

  useEffect(() => {
    if (!avatarUrl) {
      setAvatarSrc(DEFAULT_AVATAR_URL);
      return;
    }
    setAvatarSrc(avatarUrl);
  }, [avatarUrl]);

  const handleAvatarError = event => {
    if (event?.currentTarget?.src === DEFAULT_AVATAR_URL) return;
    setAvatarSrc(DEFAULT_AVATAR_URL);
  };

  const avatarNode = (
    <img
      src={avatarSrc || DEFAULT_AVATAR_URL}
      alt="پروفایل کاربر"
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        objectFit: "cover",
        border: "2px solid #ddd",
      }}
      onError={handleAvatarError}
    />
  );
  const userMenuTitle = (
    <>
      {avatarNode}
      <span className="sr-only">{`${welcomeName} خوش آمدید`}</span>
    </>
  );
  useEffect(() => {
    if (!isLoggedIn) setAvatarMenuOpen(false);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!avatarMenuOpen) return undefined;

    const handleClickOutside = event => {
      if (
        avatarMenuRef.current &&
        !avatarMenuRef.current.contains(event.target)
      ) {
        setAvatarMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [avatarMenuOpen]);

  const collapseResponsiveNavbar = () => {
    if (typeof document === "undefined") return;
    const navbarToggle = document.querySelector("#myNavbar .navbar-toggler");
    if (navbarToggle && !navbarToggle.classList.contains("collapsed")) {
      navbarToggle.click();
    }
  };

  const handleLogout = ({ collapseNav = false } = {}) => {
    const currentUrl =
      typeof window !== "undefined" ? window.location.href : "/";
    const encodedLogoutUrl = storedLoginData?.idToken
      ? `https://auth.inoor.ir/connect/endsession?id_token_hint=${encodeURIComponent(
          storedLoginData.idToken,
        )}&post_logout_redirect_uri=https://pajoohyar.ir`
      : currentUrl;

    setAvatarMenuOpen(false);
    context.cookie.remove("pajoohyar_access_token");
    context.cookie.remove("pajoohyar_expires_at");
    context.cookie.remove("pajoohyar_profile");
    context.ApiDriver.access_token = null;
    context.ApiDriver.expires_at = null;
    context.profile = null;
    clearSuccessfulLoginData();
    setStoredLoginData(null);
    setStoredProfileData(null);

    if (collapseNav) collapseResponsiveNavbar();

    window.location = encodedLogoutUrl;
  };

  return (
    <header className="myHeader bg-light">
      {/* <ScrollableAnchor id="gotop"> */}
      <Container className="bg-light">
        <Navbar
          bg="light"
          variant="light"
          id="myNavbar"
          expand="lg"
          style={{ zIndex: 100, position: "relative" }}>
          <Navbar.Brand href="https://pajoohyar.ir">
            <img
              height={40}
              alt="لوگوی پژوهیار"
              src={Pajoohyar}
              loading="lazy"
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          {isLoggedIn && (
            <div className="header-avatar-wrapper" ref={avatarMenuRef}>
              <button
                type="button"
                className="avatar-button"
                onClick={() => setAvatarMenuOpen(open => !open)}>
                {userMenuTitle}
              </button>
              {avatarMenuOpen && (
                <div className="avatar-menu">
                  <a href="/profile" className="avatar-menu-item">
                    مشاهده پروفایل
                  </a>
                  <a href="/my-workshops" className="avatar-menu-item">
                    کارگاه‌های من
                  </a>
                  <button
                    type="button"
                    className="avatar-menu-item"
                    onClick={() => handleLogout()}>
                    خروج
                  </button>
                </div>
              )}
            </div>
          )}
          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="mr-auto">
              <Nav.Link href="/">صفحه اصلی</Nav.Link>
              <NavDropdown title="دانلود" className="custom-dropdown">
                <NavDropdown.Item href="/product">
                  دانلود پژوهیار
                </NavDropdown.Item>
                <NavDropdown.Item href="/extensions">
                  افزونه مرورگر
                </NavDropdown.Item>
                <NavDropdown.Item href="/metadata">فراداده</NavDropdown.Item>
              </NavDropdown>
              <NavDropdown title="کاربردی" className="custom-dropdown">
                <NavDropdown.Item href="/supported-sites">
                  پایگاه‌های پشتیبانی شده
                </NavDropdown.Item>
                <NavDropdown.Item href="/csl">شیوه‌نامه‌ها</NavDropdown.Item>
              </NavDropdown>
              <NavDropdown title="آموزش" className="custom-dropdown">
                <NavDropdown.Item href="/help">راهنما</NavDropdown.Item>
                <NavDropdown.Item href="/faq">پرسش‌های متداول</NavDropdown.Item>
                <NavDropdown.Item href="/workshop">کارگاه</NavDropdown.Item>
              </NavDropdown>

              <Nav.Link href="/blog">بلاگ </Nav.Link>
              <Nav.Link href="/cite">استناددهی آنلاین</Nav.Link>
              {hasApiCredentials ? (
                <Nav.Link href="https://pajoohyar.ir/library/">
                  <span className="menu-item-with-badge">
                    <span className="menu-item-badge">جدید</span>
                    <span>پژوهیار آنلاین</span>
                  </span>
                </Nav.Link>
              ) : (
                <Nav.Link
                  href={loginUrl}
                  className="login"
                  onClick={() =>
                    setPostLoginRedirect(
                      `${window.location.pathname}${window.location.search}${window.location.hash}`,
                    )
                  }>
                  ورود
                </Nav.Link>
              )}
              {isLoggedIn && (
                <>
                  <Nav.Link
                    className="d-lg-none"
                    href="/profile"
                    onClick={() => collapseResponsiveNavbar()}>
                    مشاهده پروفایل
                  </Nav.Link>
                  <Nav.Link
                    className="d-lg-none"
                    href="/my-workshops"
                    onClick={() => collapseResponsiveNavbar()}>
                    کارگاه‌های من
                  </Nav.Link>
                  <Nav.Link
                    className="d-lg-none"
                    href="#logout"
                    onClick={event => {
                      event.preventDefault();
                      handleLogout({ collapseNav: true });
                    }}>
                    خروج
                  </Nav.Link>
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Navbar>
      </Container>
      {/* </ScrollableAnchor> */}
    </header>
  );
};

export default Header;
